import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  FieldArray,
  FieldArrayRenderProps,
} from "@progress/kendo-react-form";
import { Typography } from "@progress/kendo-react-common";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import SplitItemChildArray from "./SplitItemChildArray";
import { Dialog } from "@progress/kendo-react-dialogs";
import { MdDelete } from "react-icons/md";
import IconButton from "../../components/common/IconButton";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { useAppSelector } from "../../app/hooks";

const SplitItemArray = (fieldArrayRenderProps: FieldArrayRenderProps) => {
  const [deleteIndex, setDeleteIndex] = React.useState<number | null>(null);
  const fieldName = fieldArrayRenderProps.name;
  const ItemList = useAppSelector((state) => state.item.ItemList) || [];

  const pushSplitInArray = React.useCallback(() => {
    fieldArrayRenderProps?.onPush({
      value: {
        ProductID: "",
        GrossWeight: 0,
        LessWeight: 0,
        NetWeight: 0,
        ReadyDataChild: [],
      },
    });
  }, [fieldArrayRenderProps]);

  return (
    <>
      {deleteIndex !== null && (
        <Dialog onClose={() => setDeleteIndex(null)}>
          <Typography.h5>{"Delete Split Item"}</Typography.h5>
          <GridLayout>
            <GridLayoutItem>
              <Typography.p>
                Are you sure you want to delete this split item?
              </Typography.p>
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <Button
                style={{ marginRight: 4 }}
                fillMode={"outline"}
                themeColor={"primary"}
                onClick={() => setDeleteIndex(null)}
                type="button"
              >
                Cancel
              </Button>
              <Button
                fillMode={"solid"}
                themeColor={"error"}
                onClick={() => {
                  fieldArrayRenderProps.onRemove({ index: deleteIndex });
                  setDeleteIndex(null);
                }}
                type="button"
              >
                Delete
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </Dialog>
      )}

      <GridLayout cols={[{ width: "50%" }, { width: "50%" }]}>
        <GridLayoutItem>
          <Typography.h5 style={{ marginBottom: 15, fontWeight: "bold" }}>
            Split Items List
          </Typography.h5>
        </GridLayoutItem>
        <GridLayoutItem style={{ textAlign: "left", marginLeft: "auto" }}>
          <Button
            fillMode={"solid"}
            themeColor={"primary"}
            onClick={pushSplitInArray}
            type="button"
          >
            Add Split Item
          </Button>
        </GridLayoutItem>
      </GridLayout>

      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.map((split: any, index: number) => (
          <div
            key={index}
            style={{
              border: "1px solid #ccc",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 15,
              }}
            >
              <Typography.h5
                style={{ color: "#1a73e8", fontWeight: "bold", margin: 0 }}
              >
                #{index + 1}
              </Typography.h5>
              <IconButton
                type="button"
                fillMode="solid"
                size="small"
                title="Delete"
                themeColor="error"
                onClick={() => setDeleteIndex(index)}
              >
                <MdDelete
                  className="absolute-position"
                  style={{ fontSize: "22px" }}
                />
              </IconButton>
            </div>

            <GridLayout
              style={{ marginRight: 20 }}
              gap={{ rows: 0, cols: 10 }}
              cols={[
                { width: "24.6%" },
                { width: "24.6%" },
                { width: "24.6%" },
                { width: "24.6%" },
              ]}
            >
              <GridLayoutItem
                style={{
                  display: "flex",
                  marginTop: "22px",
                  justifyContent: "end",
                }}
              >
                Select Item *:
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name={`${fieldName}.${index}.ProductID`}
                  placeholder="e.g. PRD-1001"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  options={ItemList.map((item: any) => ({
                    value: item.id,
                    label: item.item_name,
                  }))}
                />
              </GridLayoutItem>
              <GridLayoutItem colSpan={2}></GridLayoutItem>

              <GridLayoutItem
                style={{
                  display: "flex",
                  marginTop: "22px",
                  justifyContent: "end",
                }}
              >
                Gross Weight (gm) *:
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name={`${fieldName}.${index}.GrossWeight`}
                  placeholder="0.000"
                  format="n3"
                  component={FormNumericTextField}
                  validator={requiredValidator}
                  onChange={(e: any) => {
                    const gross = e.value || 0;
                    const less = Number(
                      fieldArrayRenderProps.formRenderProps.valueGetter(
                        `${fieldName}.${index}.LessWeight`,
                      ) || 0,
                    );
                    fieldArrayRenderProps.formRenderProps.onChange(
                      `${fieldName}.${index}.NetWeight`,
                      { value: gross - less },
                    );
                  }}
                />
              </GridLayoutItem>
              <GridLayoutItem colSpan={2}></GridLayoutItem>

              <GridLayoutItem
                style={{
                  display: "flex",
                  marginTop: "22px",
                  justifyContent: "end",
                }}
              >
                Less Weight (gm):
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name={`${fieldName}.${index}.LessWeight`}
                  placeholder="0.000"
                  format="n3"
                  component={FormNumericTextField}
                  disabled={true}
                />
              </GridLayoutItem>
              <GridLayoutItem colSpan={2}></GridLayoutItem>

              <GridLayoutItem
                style={{
                  display: "flex",
                  marginTop: "22px",
                  justifyContent: "end",
                }}
              >
                Net Weight (gm):
              </GridLayoutItem>
              <GridLayoutItem>
                <Field
                  name={`${fieldName}.${index}.NetWeight`}
                  placeholder="0.000"
                  format="n3"
                  component={FormNumericTextField}
                  disabled={true}
                />
              </GridLayoutItem>
              <GridLayoutItem colSpan={2}></GridLayoutItem>
            </GridLayout>

            {/* Nested Child Items Array for this Split */}
            <FieldArray
              formRenderProps={fieldArrayRenderProps.formRenderProps}
              component={SplitItemChildArray}
              name={`${fieldName}.${index}.ReadyDataChild`}
              splitIndex={index}
              parentFieldName={fieldName}
            />
          </div>
        ))}
    </>
  );
};

export default SplitItemArray;
